/**
 * Template loader service for MCP project generation
 */

export interface TemplateVariables {
  PROJECT_NAME: string;
  PROJECT_NAME_UNDERSCORES: string;
  API_TITLE: string;
  API_DESCRIPTION: string;
  API_BASE_URL: string;
  CLIENT_CLASS_NAME: string;
  TOOL_FUNCTIONS: string;
  CLIENT_METHODS: string;
  TEST_FUNCTIONS: string;
  CLIENT_TEST_FUNCTIONS: string;
  TOOLS_DOCUMENTATION: string;
}

export interface TemplateFile {
  path: string;
  content: string;
}

export class TemplateLoader {
  private static templateCache: Map<string, string> = new Map();

  /**
   * Load all template files for MCP server generation
   */
  static async loadTemplates(): Promise<TemplateFile[]> {
    const templateFiles = [
      'src/mcp_server_PROJECT_NAME/__init__.py',
      'src/mcp_server_PROJECT_NAME/server.py',
      'src/mcp_server_PROJECT_NAME/client.py',
      'src/mcp_server_PROJECT_NAME/schemas.py',
      'tests/__init__.py',
      'tests/test_server.py',
      'tests/test_client.py',
      'tests/conftest.py',
      'pyproject.toml',
      'README.md',
      'requirements.txt',
      '.env.example',
      'mcp.json',
      'claude_desktop_config.json',
      'vscode_settings.json',
      'Makefile'
    ];

    const templates: TemplateFile[] = [];

    for (const filePath of templateFiles) {
      try {
        const content = await this.loadTemplate(filePath);
        templates.push({ path: filePath, content });
      } catch (error) {
        console.warn(`Failed to load template ${filePath}:`, error);
        // Fallback to hardcoded templates if needed
        const fallbackContent = this.getFallbackTemplate(filePath);
        if (fallbackContent) {
          templates.push({ path: filePath, content: fallbackContent });
        }
      }
    }

    return templates;
  }

  /**
   * Load a single template file
   */
  private static async loadTemplate(filePath: string): Promise<string> {
    const cacheKey = filePath;
    
    if (this.templateCache.has(cacheKey)) {
      return this.templateCache.get(cacheKey)!;
    }

    try {
      // Try to load from public templates folder
      const response = await fetch(`/templates/mcp-server/${filePath}`);
      
      if (!response.ok) {
        console.warn(`Failed to load template ${filePath}: ${response.statusText}`);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const content = await response.text();
      
      // Validate that we got actual template content, not an error page
      if (content.includes('<!DOCTYPE html>') || content.includes('<html')) {
        throw new Error(`Received HTML instead of template content for ${filePath}`);
      }
      
      this.templateCache.set(cacheKey, content);
      console.log(`Successfully loaded template: ${filePath}`);
      return content;
    } catch (error) {
      console.error(`Failed to load template ${filePath}:`, error);
      throw error;
    }
  }

  /**
   * Process templates with variable substitution
   */
  static processTemplates(templates: TemplateFile[], variables: TemplateVariables): Record<string, string> {
    const processedFiles: Record<string, string> = {};

    for (const template of templates) {
      const processedPath = this.substituteVariables(template.path, variables);
      const processedContent = this.substituteVariables(template.content, variables);
      
      processedFiles[processedPath] = processedContent;
    }

    return processedFiles;
  }

  /**
   * Substitute template variables in content
   */
  private static substituteVariables(content: string, variables: TemplateVariables): string {
    let result = content;
    
    for (const [key, value] of Object.entries(variables)) {
      const regex = new RegExp(`{{${key}}}`, 'g');
      result = result.replace(regex, value);
    }
    
    return result;
  }

  /**
   * Generate variables from OpenAPI spec and operations
   */
  static generateVariables(spec: any, operations: any[], projectName: string): TemplateVariables {
    const projectNameUnderscores = projectName.replace(/-/g, '_');
    const clientClassName = projectNameUnderscores
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join('') + 'Client';

    // Generate tool functions
    const toolFunctions = operations.map(op => this.generateToolFunction(op)).join('\n\n');
    
    // Generate client methods
    const clientMethods = operations.map(op => this.generateClientMethod(op)).join('\n\n');
    
    // Generate test functions
    const testFunctions = operations.map(op => this.generateTestFunction(op)).join('\n\n');
    
    // Generate client test functions
    const clientTestFunctions = operations.map(op => this.generateClientTestFunction(op)).join('\n\n');
    
    // Generate tools documentation
    const toolsDocumentation = operations.map(op => 
      `- **${op.operationId}**: ${op.summary || op.description || `${op.method} ${op.path}`}`
    ).join('\n');

    return {
      PROJECT_NAME: projectName,
      PROJECT_NAME_UNDERSCORES: projectNameUnderscores,
      API_TITLE: spec.info.title,
      API_DESCRIPTION: spec.info.description || 'Generated API client',
      API_BASE_URL: spec.servers?.[0]?.url || 'https://api.example.com',
      CLIENT_CLASS_NAME: clientClassName,
      TOOL_FUNCTIONS: toolFunctions,
      CLIENT_METHODS: clientMethods,
      TEST_FUNCTIONS: testFunctions,
      CLIENT_TEST_FUNCTIONS: clientTestFunctions,
      TOOLS_DOCUMENTATION: toolsDocumentation
    };
  }

  /**
   * Generate MCP tool function for an operation
   */
  private static generateToolFunction(operation: any): string {
    const params = operation.method === 'GET' ? 'params' : 'data';
    return `@mcp.tool()
async def ${operation.operationId}(${params}: dict = None) -> str:
    """${operation.summary || operation.description || `${operation.method} ${operation.path}`}"""
    try:
        result = await client.${operation.operationId.toLowerCase()}(**${params} if ${params} else {})
        return str(result)
    except Exception as e:
        raise Exception(f"API call failed: {str(e)}")`;
  }

  /**
   * Generate client method for an operation
   */
  private static generateClientMethod(operation: any): string {
    const methodCall = operation.method === 'GET' 
      ? `return await self._make_request("${operation.method}", "${operation.path}", params=kwargs)`
      : `return await self._make_request("${operation.method}", "${operation.path}", json=kwargs)`;

    return `    async def ${operation.operationId.toLowerCase()}(self, **kwargs) -> Dict[str, Any]:
        """${operation.summary || operation.description || `${operation.method} ${operation.path}`}"""
        ${methodCall}`;
  }

  /**
   * Generate test function for an operation
   */
  private static generateTestFunction(operation: any): string {
    const testParams = operation.method === 'GET' ? 'params={"test": "value"}' : 'data={"test": "value"}';
    
    return `    @pytest.mark.asyncio
    async def test_${operation.operationId.toLowerCase()}_success(self):
        """Test successful ${operation.operationId} operation"""
        expected_result = {"status": "success", "data": "test_data"}
        
        with patch.object(client, '${operation.operationId.toLowerCase()}', return_value=expected_result):
            result = await ${operation.operationId}(${testParams})
            
            assert str(expected_result) in result
            client.${operation.operationId.toLowerCase()}.assert_called_once()
    
    @pytest.mark.asyncio 
    async def test_${operation.operationId.toLowerCase()}_error(self):
        """Test error handling for ${operation.operationId} operation"""
        with patch.object(client, '${operation.operationId.toLowerCase()}', side_effect=Exception("API Error")):
            with pytest.raises(Exception, match="API call failed"):
                await ${operation.operationId}(${testParams})`;
  }

  /**
   * Generate client test function for an operation
   */
  private static generateClientTestFunction(operation: any): string {
    const testKwargs = operation.method === 'GET' ? '**{"param": "value"}' : '**{"data": "value"}';
    
    return `    @pytest.mark.asyncio
    async def test_${operation.operationId.toLowerCase()}_success(self, client, mock_response):
        """Test successful ${operation.operationId} API call"""
        with aioresponses() as m:
            url = f"{client.base_url}${operation.path}"
            m.${operation.method.toLowerCase()}(url, payload=mock_response)
            
            result = await client.${operation.operationId.toLowerCase()}(${testKwargs})
            
            assert result == mock_response
    
    @pytest.mark.asyncio
    async def test_${operation.operationId.toLowerCase()}_http_error(self, client):
        """Test ${operation.operationId} HTTP error handling"""
        with aioresponses() as m:
            url = f"{client.base_url}${operation.path}"
            m.${operation.method.toLowerCase()}(url, status=404)
            
            with pytest.raises(Exception):
                await client.${operation.operationId.toLowerCase()}(${testKwargs})`;
  }

  /**
   * Fallback templates if loading fails
   */
  private static getFallbackTemplate(filePath: string): string | null {
    // Return comprehensive fallback templates if remote loading fails
    if (filePath.includes('__init__.py')) {
      return '# Package initialization';
    }
    
    if (filePath === 'requirements.txt') {
      return 'fastmcp>=0.2.0\naiohttp>=3.9.0\npydantic>=2.0.0';
    }

    if (filePath.includes('server.py')) {
      return `"""
MCP Server for {{API_TITLE}}
Generated from OpenAPI specification using FastMCP
"""

import asyncio
import logging
from datetime import datetime
from typing import Any, Dict, List, Optional

from fastmcp import FastMCP
from .client import {{CLIENT_CLASS_NAME}}

# Configure logging
logging.basicConfig(level=logging.INFO)
logger = logging.getLogger(__name__)

# Initialize FastMCP
mcp = FastMCP("{{PROJECT_NAME}}")

# Initialize API client
client = {{CLIENT_CLASS_NAME}}()

{{TOOL_FUNCTIONS}}

async def main():
    """Run the MCP server"""
    async with mcp:
        await mcp.run()

if __name__ == "__main__":
    asyncio.run(main())`;
    }

    if (filePath.includes('client.py')) {
      return `"""
API Client for {{API_TITLE}}
Generated from OpenAPI specification
"""

import asyncio
import logging
from typing import Any, Dict, List, Optional
import aiohttp
import os
from urllib.parse import urljoin

logger = logging.getLogger(__name__)

class {{CLIENT_CLASS_NAME}}:
    def __init__(self, base_url: Optional[str] = None, api_key: Optional[str] = None):
        self.base_url = base_url or os.getenv("API_BASE_URL", "{{API_BASE_URL}}")
        self.api_key = api_key or os.getenv("API_KEY")
        self.session: Optional[aiohttp.ClientSession] = None
    
    async def _get_session(self) -> aiohttp.ClientSession:
        if self.session is None:
            headers = {"Content-Type": "application/json"}
            if self.api_key:
                headers["Authorization"] = f"Bearer {self.api_key}"
            
            self.session = aiohttp.ClientSession(
                headers=headers,
                timeout=aiohttp.ClientTimeout(total=30)
            )
        return self.session
    
    async def close(self):
        if self.session:
            await self.session.close()
            self.session = None
    
    async def _make_request(self, method: str, endpoint: str, **kwargs) -> Dict[str, Any]:
        session = await self._get_session()
        url = urljoin(self.base_url, endpoint)
        
        try:
            async with session.request(method, url, **kwargs) as response:
                response.raise_for_status()
                return await response.json()
        except aiohttp.ClientError as e:
            logger.error(f"API request failed: {e}")
            raise
    
{{CLIENT_METHODS}}`;
    }

    if (filePath === 'README.md') {
      return `# {{API_TITLE}} MCP Server

A Model Context Protocol (MCP) server for {{API_TITLE}}, generated from OpenAPI specification.

## Description

{{API_DESCRIPTION}}

## Installation

\`\`\`bash
# Install the package
pip install -e .

# Install with development dependencies
pip install -e ".[dev]"
\`\`\`

## Usage

### Running the MCP Server

\`\`\`bash
python -m mcp_server_{{PROJECT_NAME_UNDERSCORES}}.server
\`\`\`

### Available Tools

{{TOOLS_DOCUMENTATION}}

## Generated with

This MCP server was generated using the [OpenAPI to MCP Converter](https://openapi-to-mcp-converter.pages.dev/).`;
    }
    
    return null;
  }
}